import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Package,
  ShoppingBag,
  Heart,
  Share2,
  CheckCircle,
  Truck,
  Shield,
  Clock,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Loader2,
  Copy,
  Check,
  Mail,
  Facebook,
  Linkedin,
  Twitter,
  Link as LinkIcon,
  X,
  Utensils,
  SprayCan,
  Star,
  Eye,
  Info,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import { productService } from "../api/services/productService";
import { useToast } from "../components/common/ThemeToaster";
import { useWishlist } from "../context/WishlistContext";

// ========== HELPER FUNCTIONS ==========

const getFullImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  if (path.startsWith("/uploads")) {
    const apiBaseUrl =
      import.meta.env.VITE_API_UPLAODS || "http://localhost:3000";
    return `${apiBaseUrl}${path}`;
  }
  return path;
};

const extractProductImages = (product) => {
  const images = [];
  if (product.mainImagePath)
    images.push(getFullImageUrl(product.mainImagePath));
  if (product.galleryImages && product.galleryImages.length > 0) {
    product.galleryImages.forEach((img) => {
      if (img.path) images.push(getFullImageUrl(img.path));
      else if (img.data) images.push(`data:${img.mimeType};base64,${img.data}`);
    });
  }
  if (product.imageBase64 && images.length === 0)
    images.push(product.imageBase64);
  if (product.image && images.length === 0) images.push(product.image);
  return images;
};

const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    const media = window.matchMedia(query);
    setMatches(media.matches);
    const listener = (e) => setMatches(e.matches);
    media.addEventListener("change", listener);
    return () => media.removeEventListener("change", listener);
  }, [query]);
  return matches;
};

// ========== IMAGE SLIDER COMPONENT ==========
const ImageSlider = ({
  images,
  alt,
  autoPlay = true,
  interval = 3000,
  className = "",
  externalIndex,
  onIndexChange,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageError, setImageError] = useState({});
  const timerRef = useRef(null);

  // Sync with external index
  useEffect(() => {
    if (externalIndex !== undefined && externalIndex !== currentIndex) {
      setCurrentIndex(externalIndex);
    }
  }, [externalIndex]);

  // Autoplay effect - FIXED
  useEffect(() => {
    // Clear any existing timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Only start autoplay if enabled and multiple images
    if (!autoPlay || !images || images.length <= 1) return;

    // Start new timer
    timerRef.current = setInterval(() => {
      setCurrentIndex((prevIndex) => {
        const nextIndex = (prevIndex + 1) % images.length;
        if (onIndexChange) {
          onIndexChange(nextIndex);
        }
        return nextIndex;
      });
    }, interval);

    // Cleanup on unmount or when dependencies change
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [images, autoPlay, interval, onIndexChange]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
    if (onIndexChange) {
      onIndexChange(index);
    }
    // Reset autoplay timer after manual navigation
    if (timerRef.current && autoPlay) {
      clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setCurrentIndex((prevIndex) => {
          const nextIndex = (prevIndex + 1) % images.length;
          if (onIndexChange) {
            onIndexChange(nextIndex);
          }
          return nextIndex;
        });
      }, interval);
    }
  };

  const goToPrevious = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const prevIndex = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
    goToSlide(prevIndex);
  };

  const goToNext = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const nextIndex = currentIndex === images.length - 1 ? 0 : currentIndex + 1;
    goToSlide(nextIndex);
  };

  const handleImageError = (index) => {
    setImageError((prev) => ({ ...prev, [index]: true }));
  };

  if (!images || images.length === 0) return null;

  return (
    <div className={`relative w-full h-full group/slider ${className}`}>
      {/* Main Image Container */}
      <div className="relative w-full h-full overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.img
            key={currentIndex}
            src={images[currentIndex]}
            alt={`${alt} - Image ${currentIndex + 1}`}
            className="w-full h-full object-cover"
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
            onError={() => handleImageError(currentIndex)}
          />
        </AnimatePresence>

        {imageError[currentIndex] && (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900">
            <Package size={48} className="text-gray-400" />
          </div>
        )}
      </div>

      {images.length > 1 && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute left-3 top-1/2 -translate-y-1/2 p-2.5 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm rounded-full shadow-xl hover:scale-110 transition-all duration-300 z-20 opacity-100 md:opacity-0 md:group-hover/slider:opacity-100"
            aria-label="Previous image"
          >
            <ChevronLeft
              size={18}
              className="text-gray-700 dark:text-gray-200"
            />
          </button>

          <button
            onClick={goToNext}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-2.5 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm rounded-full shadow-xl hover:scale-110 transition-all duration-300 z-20 opacity-100 md:opacity-0 md:group-hover/slider:opacity-100"
            aria-label="Next image"
          >
            <ChevronRight
              size={18}
              className="text-gray-700 dark:text-gray-200"
            />
          </button>

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-20">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  goToSlide(index);
                }}
                className={`transition-all duration-300 rounded-full ${
                  index === currentIndex
                    ? "w-8 h-2.5 bg-white shadow-lg"
                    : "w-2.5 h-2.5 bg-white/50 hover:bg-white/80"
                }`}
                aria-label={`Go to image ${index + 1}`}
              />
            ))}
          </div>

          <div className="absolute top-4 right-4 px-2.5 py-1 bg-black/50 backdrop-blur-md rounded-full text-white text-xs font-medium z-20">
            {currentIndex + 1} / {images.length}
          </div>
        </>
      )}
    </div>
  );
};

// ========== THUMBNAIL GALLERY COMPONENT ==========
const ThumbnailGallery = ({ images, alt, selectedIndex, onSelect }) => {
  if (!images || images.length <= 1) return null;

  return (
    <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
      {images.map((img, idx) => (
        <button
          key={idx}
          onClick={() => onSelect(idx)}
          className={`flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 transition-all duration-300 ${
            selectedIndex === idx
              ? "border-primary-500 opacity-100 scale-105 shadow-lg ring-2 ring-primary-500/30"
              : "border-transparent opacity-60 hover:opacity-100 hover:border-primary-300 hover:scale-105"
          }`}
          aria-label={`View image ${idx + 1}`}
        >
          <img
            src={img}
            alt={`${alt} - Thumbnail ${idx + 1}`}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        </button>
      ))}
    </div>
  );
};

// ========== RELATED PRODUCT CARD ==========
const RelatedProductCard = ({ product }) => {
  const [productImages, setProductImages] = useState([]);
  const [imageLoading, setImageLoading] = useState(true);
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();
  const { wishlist: toastWishlist } = useToast();
  const isWishlisted = isInWishlist(product.id);

  useEffect(() => {
    const images = extractProductImages(product);
    setProductImages(images);
    setImageLoading(false);
  }, [product]);

  const handleWishlistToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isWishlisted) {
      removeFromWishlist(product.id);
      toastWishlist(`Removed "${product.name}" from wishlist`);
    } else {
      addToWishlist(product);
      toastWishlist(`Added "${product.name}" to wishlist`);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -6 }}
      className="group bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm rounded-2xl border border-gray-200/40 dark:border-gray-800/40 overflow-hidden hover:shadow-2xl hover:border-primary-400/30 dark:hover:border-primary-600/30 transition-all duration-500"
    >
      <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900">
        {imageLoading ? (
          <div className="w-full h-full flex items-center justify-center">
            <Loader2 size={28} className="animate-spin text-primary-500" />
          </div>
        ) : productImages.length > 1 ? (
          <ImageSlider
            images={productImages}
            alt={product.imageAlt || product.name}
            autoPlay={true}
            interval={3000}
            className="w-full h-full"
          />
        ) : productImages.length === 1 ? (
          <img
            src={productImages[0]}
            alt={product.imageAlt || product.name}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package size={40} className="text-gray-400" />
          </div>
        )}
        <div className="absolute top-3 left-3 flex flex-col gap-1.5 z-10">
          {product.isNew && (
            <span className="px-2.5 py-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs font-bold rounded-lg shadow-lg flex items-center gap-1">
              <Sparkles size={10} /> NEW
            </span>
          )}
          {product.isFeatured && (
            <span className="px-2.5 py-1 bg-gradient-to-r from-amber-500 to-orange-600 text-white text-xs font-bold rounded-lg shadow-lg flex items-center gap-1">
              <Star size={10} /> FEATURED
            </span>
          )}
        </div>

        <button
          onClick={handleWishlistToggle}
          className={`absolute top-3 right-3 p-2.5 backdrop-blur-sm rounded-xl transition-all duration-300 shadow-lg z-10 ${
            isWishlisted
              ? "bg-red-500 text-white"
              : "bg-white/80 dark:bg-gray-900/80 text-gray-500 hover:bg-primary-600 hover:text-white"
          }`}
        >
          <Heart size={16} className={isWishlisted ? "fill-current" : ""} />
        </button>
      </div>

      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-gray-900 dark:text-white text-sm line-clamp-1 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
              {product.name}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {product.category === "food"
                ? "Food & Beverages"
                : "Non-Food Products"}
            </p>
          </div>
          {/* {product.isAvailable && (
            <span className="ml-2 px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-medium rounded-full whitespace-nowrap">
              In Stock
            </span>
          )} */}
        </div>

        <Link
          to={`/products/${product.slug}`}
          className="mt-2 block w-full text-center py-2.5 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white text-sm font-semibold rounded-xl transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-[1.02]"
        >
          View Details
        </Link>
      </div>
    </motion.div>
  );
};

// ========== SHARE MODAL COMPONENT ==========
const ShareModal = ({ isOpen, onClose, product, url }) => {
  const [copied, setCopied] = useState(false);
  const { success: toastSuccess } = useToast();
  const isDesktop = useMediaQuery("(min-width: 768px)");

  const shareOptions = [
    {
      name: "Copy Link",
      icon: Copy,
      color: "from-gray-500 to-gray-600",
      action: async () => {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        toastSuccess("Link copied to clipboard!");
        setTimeout(() => setCopied(false), 2000);
      },
    },
    {
      name: "Email",
      icon: Mail,
      color: "from-blue-500 to-blue-600",
      action: () => {
        window.location.href = `mailto:?subject=Check out ${product.name}&body=${url}`;
      },
    },
    {
      name: "Facebook",
      icon: Facebook,
      color: "from-blue-600 to-blue-700",
      action: () => {
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
          "_blank",
          "width=600,height=400",
        );
      },
    },
    {
      name: "Twitter",
      icon: Twitter,
      color: "from-sky-500 to-sky-600",
      action: () => {
        window.open(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(product.name)}&url=${encodeURIComponent(url)}`,
          "_blank",
          "width=600,height=400",
        );
      },
    },
    {
      name: "LinkedIn",
      icon: Linkedin,
      color: "from-blue-700 to-blue-800",
      action: () => {
        window.open(
          `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
          "_blank",
          "width=600,height=400",
        );
      },
    },
  ];

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          />
          <motion.div
            initial={
              isDesktop
                ? { opacity: 0, scale: 0.95 }
                : { opacity: 0, y: "100%" }
            }
            animate={
              isDesktop ? { opacity: 1, scale: 1 } : { opacity: 1, y: 0 }
            }
            exit={
              isDesktop
                ? { opacity: 0, scale: 0.95 }
                : { opacity: 0, y: "100%" }
            }
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className={
              isDesktop
                ? "fixed inset-0 z-[60] flex items-center justify-center p-4"
                : "fixed bottom-0 left-0 right-0 z-[60]"
            }
          >
            <div
              className={`bg-white dark:bg-gray-900 shadow-2xl border border-gray-200/50 dark:border-gray-800/50 overflow-hidden ${isDesktop ? "rounded-2xl w-full max-w-md" : "rounded-t-3xl w-full"}`}
            >
              {!isDesktop && (
                <div className="flex justify-center pt-4 pb-2">
                  <div className="w-12 h-1.5 bg-gray-300 dark:bg-gray-600 rounded-full" />
                </div>
              )}
              <div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-gray-800">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center shadow-lg">
                    <Share2 className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                      Share Product
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[200px]">
                      {product?.name}
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors"
                >
                  <X size={20} className="text-gray-500" />
                </button>
              </div>
              <div className="p-5 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  {shareOptions.map((option) => {
                    const Icon = option.icon;
                    return (
                      <button
                        key={option.name}
                        onClick={option.action}
                        className={`flex items-center gap-3 p-3.5 rounded-xl bg-gradient-to-r ${option.color} text-white hover:shadow-lg transition-all duration-300 active:scale-[0.97]`}
                      >
                        <Icon size={20} />
                        <span className="font-medium text-sm">
                          {option.name === "Copy Link" && copied
                            ? "Copied! ✓"
                            : option.name}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                  <div className="flex items-center gap-3">
                    {product?.mainImagePath ? (
                      <img
                        src={getFullImageUrl(product.mainImagePath)}
                        alt={product.name}
                        className="w-14 h-14 rounded-xl object-cover"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center">
                        <Package className="w-7 h-7 text-white" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white line-clamp-1">
                        {product?.name}
                      </p>
                      {/* <p className="text-xs text-gray-500 dark:text-gray-400">
                        SKU: {product?.sku}
                      </p> */}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 p-3 bg-gray-100 dark:bg-gray-800 rounded-xl">
                  <LinkIcon size={16} className="text-gray-400 flex-shrink-0" />
                  <p className="text-xs text-gray-600 dark:text-gray-400 truncate flex-1 font-mono">
                    {url}
                  </p>
                  <button
                    onClick={async () => {
                      await navigator.clipboard.writeText(url);
                      setCopied(true);
                      toastSuccess("Link copied!");
                      setTimeout(() => setCopied(false), 2000);
                    }}
                    className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors flex-shrink-0"
                  >
                    {copied ? (
                      <Check size={16} className="text-green-500" />
                    ) : (
                      <Copy size={16} className="text-gray-400" />
                    )}
                  </button>
                </div>
              </div>
              {!isDesktop && (
                <div className="p-4 pt-0">
                  <button
                    onClick={onClose}
                    className="w-full py-3.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-semibold rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return createPortal(modalContent, document.body);
};

// ========== MAIN PRODUCT COMPONENT ==========
const Product = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [allImages, setAllImages] = useState([]);
  const [imageLoading, setImageLoading] = useState(true);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();
  const { wishlist: toastWishlist, info: toastInfo } = useToast();

  useEffect(() => {
    if (!slug || slug === "undefined" || slug === "null") {
      setError("Invalid product URL");
      setLoading(false);
      return;
    }
    fetchProduct();
    window.scrollTo(0, 0);
  }, [slug]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await productService.getProductBySlug(slug);

      let productData;
      if (response?.data?.product) productData = response.data.product;
      else if (response?.product) productData = response.product;
      else if (response?.data?.data) productData = response.data.data;
      else if (response?.data) productData = response.data;
      else productData = response;

      if (!productData || Object.keys(productData).length === 0) {
        throw new Error("Product not found");
      }

      setProduct(productData);

      const images = extractProductImages(productData);
      setAllImages(images);
      setImageLoading(false);

      if (productData.category) {
        await fetchRelatedProducts(productData.category, productData.id);
      }
    } catch (err) {
      console.error("Error fetching product:", err);
      setError(err.message || "Failed to load product details");
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedProducts = async (category, productId) => {
    try {
      const response = await productService.getProductsByCategory(category);
      let productsData = [];

      if (response?.data?.products) productsData = response.data.products;
      else if (response?.products) productsData = response.products;
      else if (Array.isArray(response?.data)) productsData = response.data;
      else if (Array.isArray(response)) productsData = response;

      const filtered = productsData.filter((p) => p.id !== productId);
      const shuffled = [...filtered];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }

      setRelatedProducts(shuffled.slice(0, 4));
    } catch (err) {
      console.error("Error fetching related products:", err);
    }
  };

  const isWishlisted = product ? isInWishlist(product.id) : false;

  const handleWishlistToggle = () => {
    if (isWishlisted) {
      removeFromWishlist(product.id);
      toastWishlist(`Removed "${product.name}" from wishlist`);
    } else {
      addToWishlist(product);
      toastWishlist(`Added "${product.name}" to wishlist`);
    }
  };

  const handleRequestInquiry = () => {
    if (product) {
      navigate(`/inquiry?productId=${product.id}`);
      toastInfo(`Starting inquiry for ${product.name}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 flex items-center justify-center rounded-lg">
        <div className="text-center space-y-4">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
            className="w-20 h-20 mx-auto rounded-full border-4 border-primary-200 dark:border-primary-800 border-t-primary-600 dark:border-t-primary-400"
          />
          <p className="text-lg font-medium text-gray-600 dark:text-gray-400">
            Loading product details...
          </p>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white dark:from-gray-950 dark:to-gray-900 flex items-center justify-center p-6 rounded-lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-lg"
        >
          <div className="w-28 h-28 mx-auto mb-8 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center shadow-lg">
            <AlertCircle size={56} className="text-red-500" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Product Not Found
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-8 leading-relaxed">
            {error ||
              `The product "${slug}" doesn't exist or has been removed.`}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/products"
              className="px-8 py-3.5 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl font-semibold hover:shadow-xl transition-all duration-300"
            >
              Browse Products
            </Link>
            <button
              onClick={() => navigate(-1)}
              className="px-8 py-3.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-semibold hover:shadow-lg transition-all duration-300"
            >
              Go Back
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 rounded-lg">
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-1/2 -left-1/4 w-full h-full bg-gradient-to-br from-primary-200/10 to-secondary-200/10 dark:from-primary-900/10 dark:to-secondary-900/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-1/2 -right-1/4 w-full h-full bg-gradient-to-tl from-primary-300/5 to-secondary-300/5 dark:from-primary-800/5 dark:to-secondary-800/5 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="mb-8"
          >
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2.5 px-5 py-2.5 backdrop-blur-sm rounded-xl text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-300 shadow-sm hover:shadow-md group"
            >
              <ArrowLeft
                size={18}
                className="group-hover:-translate-x-1 transition-transform"
              />
              <span className="font-medium">Back</span>
            </button>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
            {/* Left Column - Product Images */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-4"
            >
              <div className="relative bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm rounded-3xl border border-gray-200/30 dark:border-gray-800/30 overflow-hidden shadow-xl shadow-gray-200/50 dark:shadow-gray-900/50">
                <div className="aspect-square overflow-hidden">
                  {imageLoading ? (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900">
                      <Loader2
                        size={48}
                        className="animate-spin text-primary-500"
                      />
                    </div>
                  ) : allImages.length > 1 ? (
                    <ImageSlider
                      images={allImages}
                      alt={product.imageAlt || product.name}
                      autoPlay={true}
                      interval={4000}
                      externalIndex={selectedImageIndex}
                      onIndexChange={setSelectedImageIndex}
                    />
                  ) : allImages.length === 1 ? (
                    <img
                      src={allImages[0]}
                      alt={product.imageAlt || product.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900">
                      <Package size={80} className="text-gray-400" />
                    </div>
                  )}
                </div>

                <div className="absolute top-5 left-5 flex flex-col gap-2 z-10">
                  {product.isNew && (
                    <span className="px-3.5 py-1.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs font-bold rounded-xl shadow-lg flex items-center gap-1.5 backdrop-blur-sm">
                      <Sparkles size={12} /> NEW
                    </span>
                  )}
                  {product.isFeatured && (
                    <span className="px-3.5 py-1.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white text-xs font-bold rounded-xl shadow-lg flex items-center gap-1.5 backdrop-blur-sm">
                      <Star size={12} /> FEATURED
                    </span>
                  )}
                </div>

                <div className="absolute top-5 right-5 flex flex-col gap-2 z-10">
                  <button
                    onClick={handleWishlistToggle}
                    className={`p-3 rounded-xl backdrop-blur-sm transition-all duration-300 shadow-lg ${
                      isWishlisted
                        ? "bg-red-500 text-white"
                        : "bg-white/80 dark:bg-gray-900/80 text-gray-500 hover:bg-primary-600 hover:text-white"
                    }`}
                  >
                    <Heart
                      size={20}
                      className={isWishlisted ? "fill-current" : ""}
                    />
                  </button>
                  <button
                    onClick={() => setShowShareModal(true)}
                    className="p-3 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm rounded-xl text-gray-500 hover:bg-primary-600 hover:text-white transition-all duration-300 shadow-lg"
                  >
                    <Share2 size={20} />
                  </button>
                </div>
              </div>

              <ThumbnailGallery
                images={allImages}
                alt={product.imageAlt || product.name}
                selectedIndex={selectedImageIndex}
                onSelect={setSelectedImageIndex}
              />
            </motion.div>

            {/* Right Column - Product Details */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-8"
            >
              {/* Category Badge */}
              <Link
                to={`/categories/${product.category === "food" ? "food" : "non-food"}`}
                className="inline-flex items-center gap-2.5 px-4 py-2 bg-primary-50 dark:bg-primary-900/20 border border-primary-200/50 dark:border-primary-800/30 rounded-full hover:shadow-md transition-all duration-300 group"
              >
                {product.category === "food" ? (
                  <Utensils
                    size={16}
                    className="text-primary-600 dark:text-primary-400"
                  />
                ) : (
                  <SprayCan
                    size={16}
                    className="text-primary-600 dark:text-primary-400"
                  />
                )}
                <span className="text-sm font-semibold text-primary-700 dark:text-primary-300 group-hover:text-primary-800 dark:group-hover:text-primary-200 transition-colors">
                  {product.category === "food"
                    ? "Food & Beverages"
                    : "Non-Food Products"}
                </span>
              </Link>

              {/* Product Title */}
              <div>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-3 leading-tight">
                  {product.name}
                </h1>
                <div className="flex items-center gap-4 text-sm">
                  {/* <span className="px-3 py-1 bg-gray-100 dark:bg-gray-800 rounded-full text-gray-600 dark:text-gray-400 font-mono">
                    SKU: {product.sku}
                  </span> */}
                  {product.isAvailable ? (
                    // <span className="flex items-center gap-1.5 text-green-600 dark:text-green-400 font-medium">
                    //   <CheckCircle size={16} />
                    //   In Stock
                    // </span>
                    <></>
                  ) : (
                    <span className="flex items-center gap-1.5 text-red-600 dark:text-red-400 font-medium">
                      <AlertCircle size={16} />
                      Unavailable
                    </span>
                  )}
                </div>
              </div>

              {/* Rating & Views */}
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2 bg-gray-100/80 dark:bg-gray-800/80 px-4 py-2 rounded-full">
                  <div className="flex items-center gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        size={14}
                        className={
                          i < 4
                            ? "fill-yellow-400 text-yellow-400"
                            : "fill-gray-300 text-gray-300 dark:fill-gray-700"
                        }
                      />
                    ))}
                  </div>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">
                    4.5
                  </span>
                </div>
                {product.viewCount > 0 && (
                  <div className="flex items-center gap-2 bg-gray-100/80 dark:bg-gray-800/80 px-4 py-2 rounded-full text-sm text-gray-600 dark:text-gray-400">
                    <Eye size={14} />
                    <span>{product.viewCount.toLocaleString()} views</span>
                  </div>
                )}
              </div>

              {/* Description */}
              <div className="bg-gradient-to-br from-gray-50 to-white dark:from-gray-800/50 dark:to-gray-900/50 rounded-2xl p-6 border border-gray-200/30 dark:border-gray-800/30">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <Info size={18} className="text-primary-500" />
                  Description
                </h3>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                  {product.description ||
                    product.shortDescription ||
                    "No description available."}
                </p>
              </div>

              {/* Specifications */}
              {product.specifications &&
                Object.keys(product.specifications).length > 0 && (
                  <div className="bg-gradient-to-br from-gray-50 to-white dark:from-gray-800/50 dark:to-gray-900/50 rounded-2xl p-6 border border-gray-200/30 dark:border-gray-800/30">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      Specifications
                    </h3>
                    <div className="space-y-2">
                      {Object.entries(product.specifications).map(
                        ([key, value]) => (
                          <div
                            key={key}
                            className="flex justify-between items-center py-2.5 px-4 bg-white/50 dark:bg-gray-900/50 rounded-xl"
                          >
                            <span className="text-sm font-medium text-gray-600 dark:text-gray-400 capitalize">
                              {key.replace(/_/g, " ")}
                            </span>
                            <span className="text-sm font-semibold text-gray-900 dark:text-white">
                              {String(value)}
                            </span>
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                )}

              {/* Features */}
              {product.features && product.features.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    Key Features
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {product.features.map((feature, idx) => (
                      <span
                        key={idx}
                        className="px-4 py-2 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 text-sm rounded-xl border border-primary-200/50 dark:border-primary-800/30 flex items-center gap-1.5"
                      >
                        <CheckCircle size={14} />
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleRequestInquiry}
                  className="flex-1 py-4 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white font-semibold rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.02] flex items-center justify-center gap-2.5"
                >
                  <ShoppingBag size={20} />
                  Send Inquiry
                </button>
                <button
                  onClick={handleWishlistToggle}
                  className={`px-5 py-4 rounded-2xl border-2 transition-all duration-300 shadow-md hover:shadow-lg ${
                    isWishlisted
                      ? "bg-red-500 border-red-500 text-white"
                      : "bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-500 hover:border-primary-500 hover:text-primary-500"
                  }`}
                >
                  <Heart
                    size={20}
                    className={isWishlisted ? "fill-current" : ""}
                  />
                </button>
                <button
                  onClick={() => setShowShareModal(true)}
                  className="px-5 py-4 bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-700 rounded-2xl text-gray-500 hover:border-primary-500 hover:text-primary-500 transition-all duration-300 shadow-md hover:shadow-lg"
                >
                  <Share2 size={20} />
                </button>
              </div>
            </motion.div>
          </div>

          {/* Related Products */}
          {relatedProducts.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mt-20"
            >
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-50 dark:bg-primary-900/20 rounded-full mb-4">
                  <TrendingUp size={16} className="text-primary-500" />
                  <span className="text-sm font-semibold text-primary-700 dark:text-primary-300">
                    You May Also Like
                  </span>
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3">
                  Similar Products
                </h2>
                <p className="text-gray-500 dark:text-gray-400 max-w-lg mx-auto">
                  Discover more products that match your interests
                </p>
                <div className="w-24 h-1 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full mx-auto mt-6" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {relatedProducts.map((relatedProduct) => (
                  <RelatedProductCard
                    key={relatedProduct.id}
                    product={relatedProduct}
                  />
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </div>

      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        product={product}
        url={window.location.href}
      />
    </>
  );
};

export default Product;
